import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Badge, Button, Checkbox, DateRangePicker, FilterChip, Modal, Tag, Tooltip } from '@indipia/ui-core';
import { getSiteColor, useAuthStore } from '@indipia/shared';
import { worklogApi } from '../api/worklogApi';
import { custReqReportApi } from '../api/custReqReportApi';
import { custReqErrorMessage } from '@indipia/shared-features';
import { CustReqReportBlock } from '../components/custReq/CustReqReportBlock';
import { CustReqConfirmList } from '../components/custReq/CustReqConfirmList';
import { checkedNote, countSummary, hasCustReq, patchApplied } from '../components/custReq/custReqReport.logic';
import { useWorklogStore, type WorklogCode } from '../store/worklogStore';
import { ThumbImg } from '../components/ThumbImg';
import { Toast } from '../components/Toast';
import { PhotoLightbox } from '../components/PhotoLightbox';
import { useFitThumbCount } from '../hooks/useFitThumbCount';
import { useWorkTypeName } from '../hooks/useWorkTypeName';
import { useHolidays } from '../hooks/useHolidays';
import { presetRange, DATE_PRESETS, type DatePreset as Preset } from '../config/datePreset';
import { SitePanelProvider } from '../components/site-panel/SitePanelContext';
import { SiteArea } from '../components/site-panel/SiteArea';
import { useSiteSelection } from '../components/site-panel/useSiteSelection';
import type {
  BasicInfoPartner,
  FieldReportListItem,
} from '../types/worklog.types';

/**
 * 현장보고 목록(확인) — 사장님 콘솔 (기능명세 C-3, API명세 §3.1).
 *
 * Z(반장 외부화면)와 사용주체·진입경로가 다르다: 사장님 JWT 로그인, 인디공사현황 메뉴 진입.
 * 좌측 현장 패널(SiteArea)에서 현장을 선택/해제하면 우측 목록을 siteId로 필터한다.
 * 상세 페이지는 폐기(§5.6) — 카드 내 아코디언(본문·사진 더보기)과 라이트박스로 대체한다.
 *
 * 사장님 열람(미확인/확인)은 관리하지 않는다(2026-10-21 결정, 설계서 S11~S13 §3.2) — 카드를 눌러도 읽음 처리를
 * 하지 않고, 미확인 칩 · 확인/미확인 배지 · 홈 '미확인' 진입이 없다. 카드 우측에는 처리 상태 배지만 보인다:
 * 작업완료(이슈 없음) 보고 = 검수 대기 → 검수완료, 이슈 보고 = 이슈보고 → 조치완료, 진행중 보고 = 배지 없음.
 * [검수완료] · [조치완료]는 확인 팝업을 거친다(화면기획서 004 D).
 *
 * 서버 조회조건(§3.1 기간 모드): reportDateFrom/To(필수) · worklogId · partnerId[] · issueTypeCode[].
 * 클라이언트 필터: 현장(siteId) · 처리상태(검수 대기 / 이슈) — §3.1이 명시한 분담.
 */

const PAGE_SIZE = 20;
/** 작성자 필터의 로그인 사장님(OWNER) pseudo-partner 식별자 — 실제 partnerId와 안 겹치는 음수. 조회 시 includeOwner로 변환. */
const OWNER_AUTHOR_ID = -1;
/** 알림 딥링크(결정 3-8) 하이라이트 지속시간 — 고객대시보드 패턴과 동일 1.5초 */
const HIGHLIGHT_MS = 1500;

type StatusFilter = 'all' | 'insp' | 'issue';
type LoadState = 'loading' | 'ready' | 'error';

/** 이슈 보고 여부 — 서버 issueYn 우선, 없으면 이슈유형 코드로 판정 */
const isIssueReport = (it: FieldReportListItem) =>
  it.issueYn ? it.issueYn === 'Y' : !!it.issueTypeCodes?.length;
/**
 * 검수완료 여부 — 서버 approvalYn. 사장님 직접 작성 보고(OWNER)는 검수할 상대가 없어 저장 때부터 검수완료다(설계서 S11~S13 §3.2).
 * 서버가 새 보고에는 'Y'를 저장하지만 배포 전 기존 보고는 보정 전까지 'N'이라, 화면에서도 출처로 같이 판정한다.
 */
const isApproved = (it: FieldReportListItem) => it.approvalYn === 'Y' || it.reportSource === 'OWNER';
/** 검수 대기 탭 소속 — 작업완료 + 이슈 없음 + 검수 미완료 */
const isInspPending = (it: FieldReportListItem) =>
  it.workCmplYn === 'Y' && !isIssueReport(it) && !isApproved(it);
/** 이슈 탭 소속 — 이슈 보고 + 조치 미완료 */
const isIssuePending = (it: FieldReportListItem) => isIssueReport(it) && it.resolvedYn !== 'Y';

const pad2 = (n: number) => String(n).padStart(2, '0');
const toDash = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const DOW = ['일', '월', '화', '수', '목', '금', '토'];

/** 보고일시 ISO → 날짜 키 'YYYY-MM-DD' (일자 그룹핑용) */
function dateKey(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
/** ISO → '2026.06.11 (목)' 날짜 그룹 라벨 (작업요청·공사현황과 통일) */
function fmtDateLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getFullYear()}.${pad2(d.getMonth() + 1)}.${pad2(d.getDate())} (${DOW[d.getDay()]})`;
}
/** ISO → '2026.10.06 10:34'(24시간) — 카드 '작성일시' */
function fmtReportDtm(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getFullYear()}.${pad2(d.getMonth() + 1)}.${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}
/** 'YYYY-MM-DD' → '2026.10.06' — 카드 '작업일' */
function fmtWorkDate(date: string): string {
  return date.slice(0, 10).replace(/-/g, '.');
}
// 일자 그룹 — 보고일시(reportDtm) 기준. visibleItems 순서를 보존해 정렬(최신/과거순)이 그대로 반영된다.
interface DateGroup {
  key: string;
  sample: string; // 대표 reportDtm (날짜 라벨용)
  items: FieldReportListItem[];
}
function groupByDate(items: FieldReportListItem[]): DateGroup[] {
  const map = new Map<string, DateGroup>();
  for (const it of items) {
    const dk = dateKey(it.reportDtm);
    let dg = map.get(dk);
    if (!dg) {
      dg = { key: dk, sample: it.reportDtm, items: [] };
      map.set(dk, dg);
    }
    dg.items.push(it);
  }
  return [...map.values()];
}

export function FieldReportListPage() {
  return (
    <SitePanelProvider>
      <FieldReportListInner />
    </SitePanelProvider>
  );
}

function FieldReportListInner() {
  // 알림 딥링크(결정 3-8) — SITE_REPORT_RECEIVED 알림 클릭 시 ?targetId=fieldReportId로 랜딩.
  // 상세 라우트가 폐기(§5.6)돼 경로 세그먼트가 아니라 쿼리스트링으로 받는다.
  const [searchParams, setSearchParams] = useSearchParams();
  const targetId = Number(searchParams.get('targetId')) || null;
  // 내부 딥링크 ?f=insp|issue (D-03 §8.3) — 최초 1회 읽어 초기 탭으로 쓰고 주소에서 제거한다. 알 수 없는 값은 무시.
  const [initialF] = useState<StatusFilter | null>(() => {
    const f = searchParams.get('f');
    return f === 'insp' || f === 'issue' ? f : null;
  });
  // 내부 딥링크 ?siteId= — 그 현장을 처음 선택된 현장으로 연다(인디플래너 신호 팝업 [검수하기] · [조치하기] 등). 최초 1회만 읽고 주소에서 제거한다.
  const [linkSiteId] = useState<number | null>(() => {
    const n = Number(searchParams.get('siteId'));
    return Number.isInteger(n) && n > 0 ? n : null;
  });
  useEffect(() => {
    if (!searchParams.has('f') && !searchParams.has('siteId')) return;
    const next = new URLSearchParams(searchParams);
    next.delete('f');
    next.delete('siteId');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // ── 조회 조건(서버) — 기간(날짜)·작성자만 서버 조회조건. 처리상태(상태칩)는 조회조건이 아니라 결과 필터라 기간과 독립.
  const [preset, setPreset] = useState<Preset>('7d');
  // 직접설정 기간(YYYY-MM-DD). 있으면 프리셋보다 우선한다.
  const [customRange, setCustomRange] = useState<{ from: string; to: string } | null>(null);
  const holidays = useHolidays();
  // 유효 기간: 직접설정 우선, 없으면 프리셋
  const range = customRange
    ? { from: new Date(`${customRange.from}T00:00:00`), to: new Date(`${customRange.to}T00:00:00`) }
    : presetRange(preset);
  const [sortDesc, setSortDesc] = useState(true);
  const [selectedPartners, setSelectedPartners] = useState<BasicInfoPartner[]>([]);
  const [partnerPopOpen, setPartnerPopOpen] = useState(false);
  // 이슈유형 필터(REQ-3-05·결정 3-4) — A02_FIELD_ISSUE_TYPE 공통코드(worklogStore 전용 그룹, §9.1)
  const [selectedIssueTypes, setSelectedIssueTypes] = useState<string[]>([]);
  const [issuePopOpen, setIssuePopOpen] = useState(false);
  const worklogCodes = useWorklogStore((s) => s.codes);
  const issueTypeMaster = useMemo(
    () => worklogCodes.filter((c) => c.codeGroupCode === 'A02_FIELD_ISSUE_TYPE'),
    [worklogCodes],
  );
  const issueTypeName = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of issueTypeMaster) m.set(c.codeValue, c.codeName);
    return m;
  }, [issueTypeMaster]);

  // ── 처리상태 필터 — 결과 필터(재조회 없음). ?f=insp|issue 로 열면 그 칩이 선택된 채 시작한다.
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(initialF ?? 'all');
  // 보기모드 기본값 — 실측 목업(현장보고_수신목록_고객공유.html) 진입 시 1열(col1)이 기본
  const [twoCol, setTwoCol] = useState(false);

  // ── 작성자 목록(basic-info store 반장 재사용 — 직접 조회 안 함) ──
  // 현장보고는 로그인 사장님(OWNER)도 작성하므로, 작성자 목록 맨 위에 세션의 로그인 사용자를 추가한다.
  // (담당 공정이 없어 공종 칩은 표시하지 않는다. 작업요청 목록은 반장만이라 여기서만 추가.)
  const storePartners = useWorklogStore((s) => s.reportWriters);
  const worklogStoreStatus = useWorklogStore((s) => s.status);
  const partnersLoaded = worklogStoreStatus === 'ready' || worklogStoreStatus === 'error';
  const authUser = useAuthStore((s) => s.user);
  const partners = useMemo(() => {
    if (!partnersLoaded) return null;
    if (!authUser) return storePartners;
    // 로그인 사장님 pseudo — partnerId 대신 음수 sentinel(OWNER_AUTHOR_ID)로 식별, 조회 시 includeOwner로 변환.
    const ownerOption: BasicInfoPartner = {
      partnerId: OWNER_AUTHOR_ID,
      partnerName: `${authUser.memberName} (나)`,
      partnerPhone: null,
      email: null,
      workTypeCodes: null,
    };
    return [ownerOption, ...storePartners];
  }, [partnersLoaded, authUser, storePartners]);

  // ── 결과 ──
  const [items, setItems] = useState<FieldReportListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  /** offset 페이지 번호(0-base) — BE §3.1 offset 페이징. 추가로드 시 +1 */
  const pageRef = useRef(0);
  const [hasNext, setHasNext] = useState(false);
  const [state, setState] = useState<LoadState>('loading');
  const [loadingMore, setLoadingMore] = useState(false);
  const [lb, setLb] = useState<{ frId: number; photoIdx: number } | null>(null);
  // 무한스크롤 — 스크롤 컨테이너 + 하단 센티넬(공사현황과 동일)
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // 현장목록 공통 선택 규칙(REQ-C-01, 토글 단일선택) — 3개 목록 화면 공용 훅
  const { selectedSiteId, isSiteSelected, toggleSite, selectOnlySite, onSitesLoaded: pickFirstSite } = useSiteSelection();
  // ?siteId= 로 열면 첫 현장 자동 선택 대신 그 현장을 고른다 — 현장 목록(SiteArea)이 처음 도착했을 때 한 번만. 목록에 없는 현장이면 기본 규칙(첫 현장)을 따른다.
  const linkSiteApplied = useRef(false);
  const handleSitesLoaded = (...args: Parameters<typeof pickFirstSite>) => {
    pickFirstSite(...args);
    if (linkSiteApplied.current || linkSiteId == null) return;
    linkSiteApplied.current = true;
    if (args[0].includes(linkSiteId)) selectOnlySite(linkSiteId);
  };

  const partnerIds = selectedPartners.map((p) => p.partnerId);
  const partnerKey = partnerIds.join(',');
  // 로그인 사장님(pseudo) 선택분은 partnerId가 아니라 includeOwner 신호로 분리한다(OWNER 보고는 partnerId 없음).
  const includeOwnerFilter = partnerIds.includes(OWNER_AUTHOR_ID);
  const realPartnerIds = partnerIds.filter((id) => id !== OWNER_AUTHOR_ID);

  const customKey = customRange ? `${customRange.from}~${customRange.to}` : '';
  const fetchList = useCallback(
    (opts: { append: boolean }, signal?: AbortSignal) => {
      // 서버 조회조건은 기간(날짜)·작성자뿐. 처리상태(상태칩)·정렬·보기·현장선택은 조회결과 내 화면 처리라 재조회하지 않는다.
      // §3.1은 날짜 필수 — 서버는 항상 최신순(-reportDtm)으로 페이징하고, 표시 정렬은 화면(visibleItems)에서 한다.
      const { from, to } = customRange
        ? { from: new Date(`${customRange.from}T00:00:00`), to: new Date(`${customRange.to}T00:00:00`) }
        : presetRange(preset);
      const page = opts.append ? pageRef.current + 1 : 0;
      return worklogApi
        .getFieldReports(
          {
            reportDateFrom: toDash(from),
            reportDateTo: toDash(to),
            partnerId: realPartnerIds.length ? realPartnerIds : undefined,
            includeOwner: includeOwnerFilter || undefined,
            issueTypeCode: selectedIssueTypes.length ? selectedIssueTypes : undefined,
            page,
            size: PAGE_SIZE,
            sort: '-reportDtm',
          },
          signal,
        )
        .then((res) => {
          const data = res.data.data;
          setItems((prev) => (opts.append ? [...prev, ...data.items] : data.items));
          setTotalCount(data.totalCount);
          setHasNext(data.hasNext);
          pageRef.current = page;
          setState('ready');
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [preset, customKey, partnerKey, selectedIssueTypes.join(',')],
  );

  useEffect(() => {
    if (!partnersLoaded) return;
    const controller = new AbortController();
    setState('loading');
    fetchList({ append: false }, controller.signal).catch((err) => {
      if (controller.signal.aborted || err?.code === 'ERR_CANCELED') return;
      setState('error');
    });
    return () => controller.abort();
  }, [partnersLoaded, fetchList]);

  const loadMore = useCallback(() => {
    if (!hasNext || loadingMore) return;
    setLoadingMore(true);
    fetchList({ append: true })
      .catch(() => {/* 추가 조회 실패는 조용히 무시 */})
      .finally(() => setLoadingMore(false));
  }, [hasNext, loadingMore, fetchList]);

  /**
   * 검수완료·조치완료 처리 — 확인 팝업([취소] [검수완료|조치완료])에서 확정하면 호출한다(화면기획서 004 D).
   * 응답(갱신된 상세)의 처리 필드를 목록 항목에 반영한다. 실패 시 항목은 그대로 두고 토스트로 알린다.
   * 이미 처리된 건은 서버가 멱등 성공으로 응답한다.
   */
  const [actingIds, setActingIds] = useState<Set<number>>(new Set());
  const [toast, setToast] = useState<{ message: string; variant: 'success' | 'warning' } | null>(null);
  /** 확인 팝업 대상 — 카드의 [검수완료] · [조치완료] 버튼이 연다 */
  const [confirmTarget, setConfirmTarget] = useState<{ item: FieldReportListItem; kind: 'inspect' | 'resolve' } | null>(null);
  const processReport = useCallback((fieldReportId: number, kind: 'inspect' | 'resolve') => {
    setActingIds((prev) => new Set(prev).add(fieldReportId));
    const call = kind === 'inspect' ? worklogApi.inspectFieldReport : worklogApi.resolveFieldReport;
    call(fieldReportId)
      .then((res) => {
        const d = res.data.data;
        setItems((prev) =>
          prev.map((it) =>
            it.fieldReportId === fieldReportId
              ? {
                  ...it,
                  approvalYn: d.approvalYn ?? (kind === 'inspect' ? 'Y' : it.approvalYn),
                  approvalDtm: d.approvalDtm ?? it.approvalDtm,
                  resolvedYn: d.resolvedYn ?? (kind === 'resolve' ? 'Y' : it.resolvedYn),
                  resolvedDtm: d.resolvedDtm ?? it.resolvedDtm,
                  // 고객 요청(S16) — 이 호출에서 확인으로 전환된 요청이 반영된 최신 목록
                  ...(d.customerRequests
                    ? { customerRequests: d.customerRequests, customerRequestSummary: d.customerRequestSummary ?? countSummary(d.customerRequests) }
                    : {}),
                }
              : it,
          ),
        );
        setToast({
          message: (kind === 'inspect' ? '검수완료 처리했어요' : '조치완료 처리했어요 — 신호등이 파랑 ‘작업 완료’로 바뀌어요') + checkedNote(d.customerRequestsChecked),
          variant: 'success',
        });
      })
      .catch(() =>
        setToast({
          message: kind === 'inspect' ? '검수완료 처리에 실패했습니다. 다시 시도해 주세요.' : '조치완료 처리에 실패했습니다. 다시 시도해 주세요.',
          variant: 'warning',
        }),
      )
      .finally(() =>
        setActingIds((prev) => {
          const next = new Set(prev);
          next.delete(fieldReportId);
          return next;
        }),
      );
  }, []);
  /** 사장 [반영 처리](S16) — 체크 없는 요청을 반영됨으로 전환. 이미 완료된 보고면 같은 호출에서 확인까지 전환된다 */
  const [custReqBusy, setCustReqBusy] = useState<Set<string>>(new Set());
  const applyCustReq = useCallback((fieldReportId: number, custReqId: number) => {
    const key = `${fieldReportId}:${custReqId}`;
    setCustReqBusy((prev) => new Set(prev).add(key));
    custReqReportApi
      .applyByOwner(fieldReportId, custReqId)
      .then((res) => {
        setItems((prev) => prev.map((it) => (it.fieldReportId === fieldReportId ? patchApplied(it, res.item) : it)));
        setToast({ message: `반영 처리했어요${checkedNote(res.customerRequestsChecked)}`, variant: 'success' });
      })
      .catch((err) => setToast({ message: custReqErrorMessage(err, '반영 처리에 실패했습니다. 다시 시도해 주세요.'), variant: 'warning' }))
      .finally(() =>
        setCustReqBusy((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        }),
      );
  }, []);
  const confirmProcess = () => {
    if (!confirmTarget) return;
    const { item, kind } = confirmTarget;
    setConfirmTarget(null);
    processReport(item.fieldReportId, kind);
  };

  // 알림 딥링크(결정 3-8) — 목록 로드 완료 후 ① targetId 보고가 속한 현장으로 전환 ② 그 카드로 스크롤 + 1.5초 하이라이트.
  const cardRefs = useRef(new Map<number, HTMLElement>());
  const [highlightId, setHighlightId] = useState<number | null>(null);
  const targetSiteSwitched = useRef(false);
  const highlightHandled = useRef(false);

  // ① 현장 전환 — REQ-C-01(우측 현장목록 단일선택)이라 다른 현장이 선택된 채면 대상 카드 자체가 안 그려진다.
  // items(현장 필터 적용 전 원본 로드분)에서 targetId를 찾아 그 siteId로 selectOnlySite 전환한다.
  useEffect(() => {
    if (state !== 'ready' || targetId == null || targetSiteSwitched.current) return;
    const target = items.find((it) => it.fieldReportId === targetId);
    if (!target) return; // 아직 로드분에 없음 — 다음 페이지 로드 후 재시도(무한스크롤 진행 시 effect 재실행)
    targetSiteSwitched.current = true;
    selectOnlySite(target.siteId);
  }, [state, targetId, items, selectOnlySite]);

  // 무한스크롤 — 하단 센티넬이 스크롤 뷰에 들어오면 자동으로 다음 페이지 조회(공사현황과 동일).
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || state !== 'ready' || !hasNext || loadingMore) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { root: scrollRef.current, rootMargin: '300px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [state, hasNext, loadingMore, loadMore]);

  // 화면 처리(재조회 없음): 현장(siteId)·처리상태(검수 대기 / 이슈) 필터 + 보고일시 정렬. 조회결과 내에서만 걸러·정렬한다.
  const visibleItems = useMemo(
    () => {
      const list = items
        .filter((it) => isSiteSelected(it.siteId))
        .filter((it) =>
          statusFilter === 'all' ? true
            : statusFilter === 'insp' ? isInspPending(it)
              : isIssuePending(it),
        );
      // 정렬은 표시용(클라이언트) — 로드된 결과 내 재정렬. 서버는 항상 최신순으로 페이징한다.
      return list.sort((a, b) => {
        const cmp = a.reportDtm < b.reportDtm ? -1 : a.reportDtm > b.reportDtm ? 1 : 0;
        return sortDesc ? -cmp : cmp;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, selectedSiteId, statusFilter, sortDesc],
  );

  // 검수 대기·이슈 탭 건수 — 로드된 목록 전체 기준(현장 선택과 무관)
  const inspCount = useMemo(() => items.filter(isInspPending).length, [items]);
  const issueCount = useMemo(() => items.filter(isIssuePending).length, [items]);

  // 일자 단위 그룹 — 작업요청 목록과 동일하게 날짜 헤더로 끊어 보여준다.
  const groups = useMemo(() => groupByDate(visibleItems), [visibleItems]);

  // ② 알림 딥링크(결정 3-8) 스크롤 + 하이라이트 — ①의 현장 전환으로 카드가 실제 렌더된(cardRefs에 잡힌)
  // 뒤에 실행된다. visibleItems가 바뀔 때마다(현장 전환 직후 포함) 다시 찾아본다.
  useEffect(() => {
    if (targetId == null || highlightHandled.current) return;
    const el = cardRefs.current.get(targetId);
    if (!el) return;
    highlightHandled.current = true;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightId(targetId);
    const t = setTimeout(() => setHighlightId(null), HIGHLIGHT_MS);
    return () => clearTimeout(t);
  }, [targetId, visibleItems]);

  const partnerLabel =
    selectedPartners.length === 0
      ? '+ 작성자'
      : selectedPartners.length === 1
        ? selectedPartners[0].partnerName
        : `${selectedPartners[0].partnerName} 외 ${selectedPartners.length - 1}`;

  // 라이트박스용 — §3.1 응답에 원본 url이 없을 수 있어 thumbUrl 로 폴백(BE url 추가 전에도 표시되게)
  const lbPhotos = lb === null
    ? []
    : (visibleItems.find((v) => v.fieldReportId === lb.frId)?.photos ?? []).map((p) => ({ url: p.url || p.thumbUrl }));

  return (
    <div className="flex h-full">
      <div className="flex min-w-0 flex-1 flex-col bg-surface">
        {/* ── 조회조건 바 ── */}
        <div className="flex flex-wrap items-center gap-2 border-b border-border px-6 py-3">
          {/* 기간 직접 설정 — 옆의 프리셋 칩보다 우선한다(active 로 어느 쪽이 이겼는지 표시) */}
          <DateRangePicker
            value={{ start: range?.from ?? null, end: range?.to ?? null }}
            onChange={({ start, end }) => {
              if (start && end) setCustomRange({ from: toDash(start), to: toDash(end) });
            }}
            active={!!customRange}
            holidays={holidays}
            presets={false}
            placeholder="기간 선택"
            aria-label="기간 직접 설정"
          />

          {/* 기간 프리셋 (공통 — 작업요청·공사현황과 통일) */}
          {DATE_PRESETS.map(([key, label]) => (
            <FilterChip
              key={key}
              active={!customRange && preset === key}
              onClick={() => {
                setCustomRange(null); // 프리셋 선택 시 직접설정 해제
                setPreset(key);
              }}
            >
              {label}
            </FilterChip>
          ))}

          <div className="mx-1 h-5 w-px bg-border" />

          {/* 이슈유형 필터(REQ-3-05) */}
          <div className="relative">
            <FilterChip active={selectedIssueTypes.length > 0} onClick={() => setIssuePopOpen((v) => !v)}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              {selectedIssueTypes.length === 0
                ? '+ 이슈항목'
                : selectedIssueTypes.length === 1
                  ? issueTypeName.get(selectedIssueTypes[0]) ?? selectedIssueTypes[0]
                  : `${issueTypeName.get(selectedIssueTypes[0]) ?? selectedIssueTypes[0]} 외 ${selectedIssueTypes.length - 1}`}
            </FilterChip>
            {issuePopOpen && (
              <IssueTypeFilterPopover
                selected={selectedIssueTypes}
                options={issueTypeMaster}
                onClose={() => setIssuePopOpen(false)}
                onApply={(list) => {
                  setSelectedIssueTypes(list);
                  setIssuePopOpen(false);
                }}
              />
            )}
          </div>

          {/* 발신자(반장) 필터 */}
          <div className="relative">
            <FilterChip active={selectedPartners.length > 0} onClick={() => setPartnerPopOpen((v) => !v)}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
                <path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                <circle cx="8.5" cy="7" r="4" />
                <line x1="20" y1="8" x2="20" y2="14" />
                <line x1="23" y1="11" x2="17" y2="11" />
              </svg>
              {partnerLabel}
            </FilterChip>
            {partnerPopOpen && (
              <PartnerFilterPopover
                selected={selectedPartners}
                partners={partners}
                error={worklogStoreStatus === 'error'}
                onClose={() => setPartnerPopOpen(false)}
                onApply={(list) => {
                  setSelectedPartners(list);
                  setPartnerPopOpen(false);
                }}
              />
            )}
          </div>
        </div>

        {/* ── 툴바: 처리상태 필터 + 카운트 + 정렬/보기 ── */}
        <div className="flex flex-wrap items-center justify-between gap-2 px-6 py-3">
          <div className="flex items-center gap-1.5">
            {([['all', '전체'], ['insp', '검수 대기'], ['issue', '이슈']] as [StatusFilter, string][]).map(
              ([key, label]) => (
                <FilterChip
                  key={key}
                  // 상태칩은 조회조건이 아니라 결과 필터 — API 재호출 없이 현재 목록에서 상태만 걸러낸다.
                  onClick={() => setStatusFilter(key)}
                  active={statusFilter === key}
                  // 건수는 FilterChip 표준 카운트 슬롯(.cnt)으로 — 로드된 목록 기준
                  count={
                    key === 'insp' ? (inspCount > 0 ? inspCount : undefined)
                      : key === 'issue' ? (issueCount > 0 ? issueCount : undefined)
                        : undefined
                  }
                >
                  {label}
                </FilterChip>
              ),
            )}
          </div>

          <div className="flex items-center gap-2">
            <span className="text-body-md text-text-secondary">
              <b className="text-text">{visibleItems.length}</b>
              <span className="text-text-muted"> / {totalCount}건</span>
            </span>
            {/* 정렬 — 화면기획서 실측: 2지선다 Segmented가 아니라 아이콘·라벨이 바뀌는 단일 토글 버튼(WorkOrderListPage와 통일) */}
            <Button variant="etc" size="md" onClick={() => setSortDesc((v) => !v)} aria-label="정렬">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round">
                <path d="M12 5v14" />
                {sortDesc ? <path d="M5 12l7-7 7 7" /> : <path d="M19 12l-7 7-7-7" />}
              </svg>
              {sortDesc ? '최신순' : '과거순'}
            </Button>
            {/* 보기모드 토글(1열/2열) — 작업요청·공사현황 목록과 동일 아이콘 */}
            <Button
              variant="etc"
              size="md"
              onClick={() => setTwoCol((v) => !v)}
              aria-pressed={twoCol}
              aria-label={twoCol ? '1열 보기' : '2열 보기'}
            >
              {twoCol ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <line x1="4" y1="6" x2="20" y2="6" /><line x1="4" y1="12" x2="20" y2="12" /><line x1="4" y1="18" x2="20" y2="18" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" />
                </svg>
              )}
              {twoCol ? '1열 보기' : '2열 보기'}
            </Button>
          </div>
        </div>

        {/* ── 카드 그리드 ── */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 pb-6">
          {state === 'loading' ? (
            <div className="flex items-center justify-center py-16 text-sm text-text-muted">불러오는 중입니다.</div>
          ) : state === 'error' ? (
            <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
              <p className="text-sm font-semibold text-text">현장보고를 불러올 수 없습니다</p>
              <p className="text-body-md text-text-secondary">잠시 후 다시 시도해 주세요.</p>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  // 재조회: 즉시 로딩 표시 후 현장보고 목록 재조회. 실패 시 다시 error.
                  setState('loading');
                  fetchList({ append: false }).catch(() => setState('error'));
                }}
              >
                다시 조회
              </Button>
            </div>
          ) : visibleItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-1 py-16 text-center">
              <p className="text-sm font-semibold text-text">조회 조건에 맞는 현장보고가 없습니다</p>
              <p className="text-body-md text-text-secondary">기간·작업자·현장 조건을 바꿔보세요.</p>
            </div>
          ) : (
            <div className="space-y-04 pt-02">
              {groups.map((dg) => (
                <div key={dg.key}>
                  {/*
                    날짜 그룹 라벨 (작업요청·공사현황과 통일) — 스크롤해도 목록 상단에 고정되고, 다음 날짜 헤더가 올라오면
                    이전 날짜를 밀어낸다(화면기획서 v1.8). 그룹 래퍼 안에 두어 그룹이 끝나면 헤더도 함께 빠진다.
                    z-10: z-sticky(1100)는 z-dropdown(1000)보다 높아 툴바 팝오버를 가려서 쓰지 않는다.
                    고정 박스의 위·아래 패딩(py-03)이 카드와의 간격을 겸한다(카드가 헤더 밑으로 비쳐 보이지 않게).
                  */}
                  <div className="sticky top-0 z-10 flex items-center gap-03 bg-surface py-03">
                    <span className="text-body-md font-bold text-text">{fmtDateLabel(dg.sample)}</span>
                    <span className="h-px flex-1 bg-border" />
                  </div>
                  <div className={twoCol ? 'grid grid-cols-1 gap-3 lg:grid-cols-2' : 'grid grid-cols-1 gap-3'}>
                    {dg.items.map((it) => (
                      <ReportCard
                        key={it.fieldReportId}
                        item={it}
                        highlighted={highlightId === it.fieldReportId}
                        cardRef={(el) => {
                          if (el) cardRefs.current.set(it.fieldReportId, el);
                          else cardRefs.current.delete(it.fieldReportId);
                        }}
                        acting={actingIds.has(it.fieldReportId)}
                        onInspect={() => setConfirmTarget({ item: it, kind: 'inspect' })}
                        onResolve={() => setConfirmTarget({ item: it, kind: 'resolve' })}
                        onPhoto={(photoIdx) => setLb({ frId: it.fieldReportId, photoIdx })}
                        custReqBusyIds={new Set([...custReqBusy].filter((k) => k.startsWith(`${it.fieldReportId}:`)).map((k) => Number(k.split(':')[1])))}
                        onApplyCustReq={(custReqId) => applyCustReq(it.fieldReportId, custReqId)}
                      />
                    ))}
                  </div>
                </div>
              ))}
              {/* 무한스크롤 센티넬 — 뷰에 들어오면 자동 조회 */}
              {hasNext && (
                <div ref={sentinelRef} className="flex items-center justify-center py-5 text-body-sm text-text-muted">
                  {loadingMore ? '불러오는 중입니다.' : ''}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 좌측 현장 패널 — 선택/해제로 siteId 필터 */}
      <SiteArea
        isSiteSelected={isSiteSelected}
        onToggleSite={toggleSite}
        onSitesLoaded={handleSitesLoaded}
        onSiteRegistered={selectOnlySite}
        onSiteDeleted={() => {
          // 삭제된 현장의 현장보고가 이미 조회해 둔 목록에 남아있으므로 재조회로 반영한다.
          setState('loading');
          fetchList({ append: false }).catch(() => setState('error'));
        }}
      />

      <PhotoLightbox
        photos={lbPhotos}
        index={lb?.photoIdx ?? null}
        onClose={() => setLb(null)}
        onIndexChange={(i) => setLb((prev) => (prev ? { ...prev, photoIdx: i } : prev))}
        renderImage={(photo, style) => (
          <ThumbImg url={photo.url} alt="" imgStyle={style} />
        )}
      />

      {/*
        [검수완료] · [조치완료] 확인 팝업 — 화면기획서 004 D(.slm-box 420~480px). 폭은 Modal 기본(md=420) — sm은 240px이라 문구가 끊긴다.
        취소는 기획서 .cf-btn.sub(옅은 채움 + 연한 테두리)에 해당하던 .btn-tertiary가 D-09 v2.26(ui-core 계약 v1.4.0)에서 삭제돼 secondary를 쓴다(scheduler 삭제 확인 모달 취소와 동일).
      */}
      <Modal
        open={confirmTarget !== null}
        onClose={() => setConfirmTarget(null)}
        title={confirmTarget?.kind === 'resolve' ? '조치를 완료하시겠습니까?' : '검수완료 하시겠습니까?'}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setConfirmTarget(null)}>
              취소
            </Button>
            <Button size="sm" onClick={confirmProcess}>
              {confirmTarget?.kind === 'resolve' ? '조치완료' : '검수완료'}
            </Button>
          </>
        }
      >
        {confirmTarget && (
          <p className="text-body-md leading-relaxed text-text-secondary">
            <strong className="text-text">
              "{confirmTarget.item.writerName ?? '작성자'} ·{' '}
              {confirmTarget.kind === 'resolve'
                ? (confirmTarget.item.issueTypeNames?.length ? confirmTarget.item.issueTypeNames : confirmTarget.item.issueTypeCodes ?? []).join(', ')
                : (confirmTarget.item.workTypeNames?.length ? confirmTarget.item.workTypeNames : confirmTarget.item.workTypeCodes).join(', ')}"
            </strong>{' '}
            {confirmTarget.kind === 'resolve' ? '이슈보고' : '작업완료 보고'}
          </p>
        )}
        {confirmTarget && <CustReqConfirmList items={confirmTarget.item.customerRequests} />}
      </Modal>

      <Toast message={toast?.message ?? null} variant={toast?.variant} onClose={() => setToast(null)} />
    </div>
  );
}

/**
 * 작업보고 카드 — 화면 이동 없이 카드 안에서 본문·사진을 펼쳐 보고(더보기/접기, 라이트박스), 처리 버튼으로 검수·조치한다.
 * 카드 자체를 눌러도 아무 일도 일어나지 않는다(열람 처리 폐기). 우측 배지는 처리 상태만 보인다.
 */
function ReportCard({
  item, highlighted, cardRef, onPhoto, acting, onInspect, onResolve, custReqBusyIds, onApplyCustReq,
}: {
  item: FieldReportListItem;
  highlighted: boolean;
  cardRef: (el: HTMLElement | null) => void;
  acting: boolean;
  onInspect: () => void;
  onResolve: () => void;
  onPhoto: (photoIdx: number) => void;
  /** 지금 [반영 처리] 호출 중인 고객 요청 ID */
  custReqBusyIds: ReadonlySet<number>;
  onApplyCustReq: (custReqId: number) => void;
}) {
  const [textExpanded, setTextExpanded] = useState(false);
  const [photosExpanded, setPhotosExpanded] = useState(false);

  // 본문이 1줄 클램프로 넘치는지 — CSS만으로는 JS에서 알 수 없어 scrollHeight/clientHeight로 판정
  // (WorkOrderListPage와 동일 패턴). 안 넘치면 "+더보기"를 아예 렌더하지 않는다(실측 목업 .has-more 조건부).
  const textRef = useRef<HTMLParagraphElement>(null);
  const [textOverflowing, setTextOverflowing] = useState(false);
  useEffect(() => {
    const el = textRef.current;
    if (el) setTextOverflowing(el.scrollHeight > el.clientHeight + 1);
  }, [item.reportContent]);

  // BE가 workTypeNames/issueTypeNames(이름 배열)를 코드와 같은 순서로 내려준다 — 이름 우선, 없으면 코드로 폴백.
  // (클라이언트 공통코드 조회로 직접 매핑하지 않는다 — BE가 이미 FieldReportListAssembler에서 해석해 내려줌)
  const workTypeNames = item.workTypeNames?.length ? item.workTypeNames : item.workTypeCodes;
  // 작업상태 뱃지 판정 — 상세화면과 동일 우선순위(이슈발생 > 작업완료 > 진행중).
  const isIssue = isIssueReport(item);
  const isComplete = !isIssue && item.workCmplYn === 'Y';
  const canInspect = isInspPending(item);
  const canResolve = isIssuePending(item);
  const issueNames = item.issueTypeNames?.length ? item.issueTypeNames : (item.issueTypeCodes ?? []);
  // 썸네일 노출 개수(REQ-3-04, 화면기획서 v1.8) — 카드 너비에 들어가는 만큼만 보이고, 넘치면 마지막 칸을 "+N 더보기" 타일로 쓴다.
  // 1열↔2열·창 크기가 바뀌면 개수도 다시 계산한다(useFitThumbCount). 펼치면 전부 + "접기"(칸을 넘길 때만).
  const total = item.photos.length;
  const { ref: thumbsRef, fit } = useFitThumbCount<HTMLDivElement>(64, 8, total > 0);
  const overflowing = total > fit;
  const collapsed = !photosExpanded && overflowing;
  const shown = collapsed ? item.photos.slice(0, fit - 1) : item.photos;
  const hidden = total - shown.length;

  return (
    <article
      ref={cardRef}
      className="overflow-hidden rounded-lg border border-border bg-surface-raised"
      style={highlighted ? { borderColor: 'var(--color-info)', boxShadow: '0 0 0 3px var(--color-infoSubtle)' } : undefined}
    >
      {/*
        헤더 — 실측 목업(.rcard-top/.rcard-info/.rcard-name/.rc-st) 그대로:
        작성자명(굵게) + 공종·작업완료·이슈유형 배지를 한 줄에, 그 아래 현장명 메타 줄, 우측에 처리 상태 배지만.
        (구 "현장보고" 유형 배지·별도 공종/작성자 태그 로우는 목업에 없어 제거함 — C-2 재대조와 같은 실수 반복 방지)
      */}
      <div className="flex items-start gap-3 p-4 pb-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1">
            <span className="text-body-md font-bold text-text">{item.writerName ?? '작성자'}</span>
            {workTypeNames.map((name, i) => (
              <Badge key={`wt-${name}-${i}`} status="info">{name}</Badge>
            ))}
            {isComplete && <Badge status="success">작업완료</Badge>}
            {issueNames.map((name, i) => (
              <Badge key={`issue-${name}-${i}`} status="warning">{name}</Badge>
            ))}
          </div>
          <div className="mt-[2px] flex items-center gap-1 text-caption text-text-muted">
            <span className="inline-flex min-w-0 items-center gap-1">
              <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: getSiteColor(item.siteId) }} />
              <span className="truncate">{item.siteName}</span>
            </span>
          </div>
        </div>
        {/* 처리 상태 — 작업완료(이슈 없음): 검수 대기 → 검수완료 / 이슈: 이슈보고 → 조치완료 / 진행중: 없음(화면기획서 004 D) */}
        {isComplete && (
          isApproved(item)
            ? <Badge status="success" className="shrink-0">검수완료</Badge>
            : <Badge status="warning" className="shrink-0">검수 대기</Badge>
        )}
        {isIssue && (
          item.resolvedYn === 'Y'
            ? <Badge status="done" className="shrink-0">조치완료</Badge>
            : <Badge status="danger" className="shrink-0">이슈보고</Badge>
        )}
      </div>

      {/* 작성일시(제출 시각) · 작업일 — 보고 내용 위(E 공사현황·C-3 이력 카드와 동일 표기). 라벨 연하게·값 진하게, 값 시작 위치 정렬 */}
      <dl className="grid grid-cols-[auto_1fr] items-baseline gap-x-sm gap-y-sm px-05 pb-03 text-caption">
        <dt className="text-text-muted">작성일시:</dt>
        <dd className="font-medium text-text">{fmtReportDtm(item.reportDtm)}</dd>
        <dt className="text-text-muted">작업일:</dt>
        <dd className="font-medium text-text">{fmtWorkDate(item.workDate)}</dd>
      </dl>

      {/* 본문 — 1줄 클램프 + 실제로 넘칠 때만 더보기/접기(REQ-3-04). 더보기/접기는 카드 클릭과 별개(이벤트 버블링 차단) */}
      <p
        ref={textRef}
        className={`px-4 text-body-sm leading-relaxed text-text-secondary ${(textOverflowing || textExpanded) ? 'pb-1' : 'pb-4'}`}
        style={
          textExpanded
            ? { whiteSpace: 'pre-line' }
            : { display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'pre-line' }
        }
      >
        {item.reportContent}
      </p>
      {(textOverflowing || textExpanded) && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setTextExpanded((v) => !v); }}
          className="px-4 pb-3 text-caption font-bold text-info"
        >
          {textExpanded ? '접기' : '+더보기'}
        </button>
      )}

      {total > 0 && (
        <div className="px-4 pb-4">
          <div ref={thumbsRef} className="flex flex-wrap gap-2">
            {shown.map((p, i) => (
              <button
                key={p.frPhotoId}
                type="button"
                aria-label={`사진 ${i + 1} 보기`}
                onClick={(e) => { e.stopPropagation(); onPhoto(i); }}
                className="h-10 w-10 shrink-0 overflow-hidden rounded-md bg-surface-muted p-0"
              >
                <ThumbImg url={p.thumbUrl} alt="" className="h-full w-full object-cover" />
              </button>
            ))}
            {collapsed && (
              <button
                type="button"
                aria-label={`사진 ${hidden}장 더 보기`}
                onClick={(e) => { e.stopPropagation(); setPhotosExpanded(true); }}
                className="flex h-10 w-10 shrink-0 flex-col items-center justify-center rounded-md border border-dashed text-caption font-bold leading-tight"
                style={{ background: 'var(--color-infoSubtle)', borderColor: 'var(--status-info-200)', color: 'var(--color-info)' }}
              >
                +{hidden}
                <span>더보기</span>
              </button>
            )}
          </div>
          {photosExpanded && overflowing && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setPhotosExpanded(false); }}
              className="mt-1 text-caption font-bold text-info"
            >
              접기
            </button>
          )}
        </div>
      )}

      {/* 고객 요청(S16) — '고객 요청 n · 반영 n', 펼치면 요청 원문·고객 사진, 체크 없는 요청은 주황 줄 + [반영 처리] */}
      {item.customerRequests && hasCustReq(item.customerRequestSummary, item.customerRequests.length) && (
        <CustReqReportBlock
          items={item.customerRequests}
          summary={item.customerRequestSummary ?? countSummary(item.customerRequests)}
          ownerReport={item.reportSource === 'OWNER'}
          busyIds={custReqBusyIds}
          onApply={onApplyCustReq}
        />
      )}

      {/*
        처리 버튼 — 검수 대기 보고는 [검수완료], 이슈 보고는 [반장님 전화] + [조치완료](둘 다 확인 팝업을 거친다).
        [반장님 전화]는 웹에서 비활성(모바일 앱에서만 바로 전화) — 반장님이 쓴 보고에만 보인다(사장님이 직접 쓴 보고엔 통화할 상대가 없음).
        비활성 버튼은 마우스를 못 받으므로 Tooltip(표준) 래퍼가 안내를 띄운다.
      */}
      {(canInspect || canResolve) && (
        <div className="flex items-center justify-end gap-sm px-4 pb-4">
          {canResolve && item.reportSource !== 'OWNER' && (
            <Tooltip tip="웹에서는 전화할 수 없어요 · 모바일 앱에서 눌러 전화해요">
              <Button variant="secondary" size="sm" disabled>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
                반장님 전화
              </Button>
            </Tooltip>
          )}
          <Button
            size="sm"
            disabled={acting}
            onClick={() => (canResolve ? onResolve() : onInspect())}
          >
            {canResolve ? '조치완료' : '검수완료'}
          </Button>
        </div>
      )}
    </article>
  );
}

/** 팝오버 행에서 바로 보여주는 공종 칩 최대 개수 — 초과분은 '+N' */
const MAX_TRADE_CHIPS = 3;

/**
 * 발신자(반장) 다중선택 팝오버. WorkOrderListPage 의 동명 컴포넌트와 같은 UX지만,
 * 그건 export 되지 않은 파일 로컬 컴포넌트라 재사용 불가 → 여기 로컬로 둔다(패키지 컨벤션).
 */
function PartnerFilterPopover({
  selected,
  partners,
  error,
  onApply,
  onClose,
}: {
  selected: BasicInfoPartner[];
  partners: BasicInfoPartner[] | null;
  error: boolean;
  onApply: (list: BasicInfoPartner[]) => void;
  onClose: () => void;
}) {
  const [checked, setChecked] = useState<Set<number>>(new Set(selected.map((p) => p.partnerId)));

  // 공종 코드 → 공종명 매핑 (공통코드 ZEU_WORK_TYPE)
  const workTypeName = useWorkTypeName();

  const toggle = (id: number) =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const apply = () => onApply((partners ?? []).filter((p) => checked.has(p.partnerId)));

  return (
    <>
      {/* 바깥 클릭 닫기 */}
      <div className="fixed inset-0 z-dropdown" onClick={onClose} aria-hidden />
      <div className="absolute left-0 top-full z-dropdown mt-1 w-64 rounded-lg border border-border bg-surface-raised p-3 shadow-lg">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-body-md font-semibold text-text">작성자 선택</span>
          <Button variant="ghost" size="sm" onClick={() => setChecked(new Set())}>
            전체 해제
          </Button>
        </div>
        {/* 가로 스크롤 없음(overflow-x-hidden) — 칩이 많아도 목록 폭 안에서 줄바꿈한다 */}
        <div className="max-h-56 space-y-0.5 overflow-y-auto overflow-x-hidden">
          {partners === null && !error && (
            <div className="py-6 text-center text-body-sm text-text-muted">불러오는 중입니다.</div>
          )}
          {error && <div className="py-6 text-center text-body-sm text-text-muted">작성자 목록을 불러오지 못했어요.</div>}
          {partners?.length === 0 && (
            <div className="py-6 text-center text-body-sm text-text-muted">등록된 작성자가 없어요.</div>
          )}
          {partners?.map((p) => (
            // 행 레이아웃: [체크박스][아바타][이름 / 공종 칩(아래로 줄바꿈)] — 공종이 많은 반장도 이름이 맨 위에 보이도록
            // 위쪽 기준(items-start)으로 맞춘다(칩을 이름 옆 좁은 열에 두면 행이 길어져 세로 중앙의 이름·체크박스가 가려졌다).
            <label key={p.partnerId} className="flex cursor-pointer items-start gap-2.5 rounded-md px-2 py-2 hover:bg-surface-muted">
              {/* 행 전체가 <label> 이라 bare — Checkbox 자체 label 래핑을 끄고 input만 렌더(label 중첩 방지).
                  h-7(=아바타 높이)로 감싸 첫 줄(아바타·이름) 중심에 맞춘다 */}
              <span className="flex h-7 shrink-0 items-center">
                <Checkbox bare checked={checked.has(p.partnerId)} onChange={() => toggle(p.partnerId)} />
              </span>
              {/* 성(첫글자) 아바타 */}
              <span
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-body-sm font-bold text-white"
                style={{ background: getSiteColor(p.partnerId) }}
              >
                {p.partnerName.charAt(0)}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex h-7 items-center gap-1.5 text-body-md font-medium text-text">
                  <span className="min-w-0 truncate">{p.partnerName}</span>
                  {/* 자재파트너 구분 — 시공 전용·사장님 항목은 표시 없음 */}
                  {(p.partnerRole === 'MATERIAL' || p.partnerRole === 'BOTH') && (
                    <Tag className="!px-1.5 !py-[1px] !text-caption">{p.partnerRole === 'BOTH' ? '시공·자재' : '자재'}</Tag>
                  )}
                </div>
                {/* 전문분야(취급공정) 칩 — 이름 아래에서 줄바꿈. 앞 MAX_TRADE_CHIPS개만 보이고 나머지는 '+N'(전체는 hover title) */}
                {p.workTypeCodes && (() => {
                  const names = p.workTypeCodes.split(',').map((code) => workTypeName.get(code) ?? code);
                  const rest = names.length - MAX_TRADE_CHIPS;
                  const chip = 'rounded px-1.5 py-[1px] text-caption text-text-secondary';
                  return (
                    <div className="flex flex-wrap gap-02 pb-01" title={names.join(', ')}>
                      {names.slice(0, MAX_TRADE_CHIPS).map((name, i) => (
                        <span key={`${name}-${i}`} className={chip} style={{ background: 'var(--shell-skeleton)' }}>
                          {name}
                        </span>
                      ))}
                      {rest > 0 && (
                        <span className={chip} style={{ background: 'var(--shell-skeleton)' }}>
                          +{rest}
                        </span>
                      )}
                    </div>
                  );
                })()}
              </div>
            </label>
          ))}
        </div>
        <div className="mt-3 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>
            취소
          </Button>
          <Button size="sm" onClick={apply}>
            적용
          </Button>
        </div>
      </div>
    </>
  );
}

/** 이슈유형(A02_FIELD_ISSUE_TYPE) 다중선택 팝오버 — PartnerFilterPopover와 동일 패턴(체크박스+전체해제+적용). */
function IssueTypeFilterPopover({
  selected,
  options,
  onApply,
  onClose,
}: {
  selected: string[];
  options: WorklogCode[];
  onApply: (list: string[]) => void;
  onClose: () => void;
}) {
  const [checked, setChecked] = useState<Set<string>>(new Set(selected));

  const toggle = (code: string) =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });

  const apply = () => onApply(options.filter((o) => checked.has(o.codeValue)).map((o) => o.codeValue));

  return (
    <>
      {/* 바깥 클릭 닫기 */}
      <div className="fixed inset-0 z-dropdown" onClick={onClose} aria-hidden />
      <div className="absolute left-0 top-full z-dropdown mt-1 w-64 rounded-lg border border-border bg-surface-raised p-3 shadow-lg">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-body-md font-semibold text-text">이슈항목 필터</span>
          <Button variant="ghost" size="sm" onClick={() => setChecked(new Set())}>
            전체 해제
          </Button>
        </div>
        <div className="max-h-56 space-y-0.5 overflow-auto">
          {options.length === 0 && (
            <div className="py-6 text-center text-body-sm text-text-muted">이슈유형이 없습니다.</div>
          )}
          {options.map((o) => (
            <label key={o.codeValue} className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-2 hover:bg-surface-muted">
              {/* 행 전체가 <label> 이라 bare — Checkbox 자체 label 래핑을 끄고 input만 렌더(label 중첩 방지) */}
              <Checkbox bare className="shrink-0" checked={checked.has(o.codeValue)} onChange={() => toggle(o.codeValue)} />
              <span className="text-body-md font-medium text-text">{o.codeName}</span>
            </label>
          ))}
        </div>
        <div className="mt-3 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>
            취소
          </Button>
          <Button size="sm" onClick={apply}>
            적용
          </Button>
        </div>
      </div>
    </>
  );
}
